/**
 * W9-A — czysta geometria miasteczka: linia ziemi, pas chodzenia, z-sort, drzwi.
 * Zero Phasera — testowalne unitami.
 */

/** Linia trawy — jedno źródło prawdy dla tła i posadowienia budynków. */
export const TOWN_GROUND_Y = 430;

/** Pas, w którym chodzi piesek / NPC / kotki (y ekranowy rośnie w dół). */
export const TOWN_WALK_BAND = { minY: 455, maxY: 660 } as const;

const DEFAULT_TARGET_H = 200;

/**
 * Prostokąt posadowienia budynku: origin (0.5, 1) na ziemi,
 * skala z proporcji tekstury (nie kwadrat 230×230).
 */
export function buildingPlacement(
  hotspot: { x: number; y: number },
  texture: { width: number; height: number },
  targetHeight?: number,
): { x: number; y: number; displayW: number; displayH: number; originY: 1 } {
  const tw = Math.max(1, texture.width);
  const th = Math.max(1, texture.height);
  const displayH = targetHeight ?? DEFAULT_TARGET_H;
  const displayW = displayH * (tw / th);
  return {
    x: hotspot.x,
    y: hotspot.y,
    displayW,
    displayH,
    originY: 1,
  };
}

/**
 * Jedna funkcja głębi dla wszystkiego w Town (budynki, rekwizyty, piesek, NPC, koty).
 * Ściśle rosnąca względem y (niżej na ekranie = bliżej kamery).
 */
export function depthForGroundY(y: number): number {
  return Math.round(y);
}

/**
 * Punkt, do którego piesek podchodzi przed wejściem — zawsze w pasie chodzenia.
 */
export function doorApproachPoint(hotspot: { x: number; y: number }): {
  x: number;
  y: number;
} {
  const rawY = hotspot.y + 36;
  const y = Math.min(
    TOWN_WALK_BAND.maxY,
    Math.max(TOWN_WALK_BAND.minY, rawY),
  );
  return { x: hotspot.x, y };
}

type HotspotBox = {
  sceneKey: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
};

function hotspotAabb(h: HotspotBox): {
  left: number;
  right: number;
  top: number;
  bottom: number;
} {
  const w = h.width ?? 140;
  const ht = h.height ?? 72;
  return {
    left: h.x - w / 2,
    right: h.x + w / 2,
    top: h.y - ht / 2,
    bottom: h.y + ht / 2,
  };
}

function aabbsOverlap(
  a: ReturnType<typeof hotspotAabb>,
  b: ReturnType<typeof hotspotAabb>,
): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/** Pary sceneKey budynków o nachodzących hitboxach (puste = OK). */
export function overlappingHotspots(
  hotspots: readonly HotspotBox[],
): Array<[string, string]> {
  const pairs: Array<[string, string]> = [];
  for (let i = 0; i < hotspots.length; i++) {
    const a = hotspots[i]!;
    const aa = hotspotAabb(a);
    for (let j = i + 1; j < hotspots.length; j++) {
      const b = hotspots[j]!;
      if (aabbsOverlap(aa, hotspotAabb(b))) {
        pairs.push([a.sceneKey, b.sceneKey]);
      }
    }
  }
  return pairs;
}
